import React, { useState, useEffect, useRef } from 'react'
import { Button, Typography } from '~/components/_atoms'
import ChatHeader from '../ChatHeader'
import TextMessages from '../Chat/TextMessages'
import ConnectionLost from '../Chat/ConnectionLost'
import ChatEndedSurvey from './ChatEndedSurvey'
import { useTranslation } from '~/hooks/useTranslation'
import { useCandidateChat } from '../CandidateChatContext'
import { getEffectiveExitUrl } from '../utils/getEffectiveExitUrl'

/**
 * ChatEnded component - displays when a chat has ended
 * Based on the op_next design from candidate_chat_ended.html
 */
const ChatEnded = ({
  // candidateCoachChatSurveyPath,
  afterChatPath,
  deloitteUser = false,
}) => {
  const effectiveExitUrl = getEffectiveExitUrl(afterChatPath)
  const { t } = useTranslation('coach_chat')
  const { getDarkModeClasses } = useCandidateChat()
  const [chatData, setChatData] = useState(null)
  const [hasAnsweredSurvey, setHasAnsweredSurvey] = useState(false)
  const textMessagesRef = useRef(null)
  const ratingRef = useRef(null)
  const tagsRef = useRef(null)
  const feedbackRef = useRef(null)

  // Check for disconnection reason and who ended chat from URL parameters
  const urlParams = new URLSearchParams(window.location.search)
  const disconnectionReason = urlParams.get('disconnection_reason')
  const isDisconnection = !!disconnectionReason
  const endedBy =
    urlParams.get('ended_by') || chatData?.chatEndedBy || 'candidate'

  // Load chat data from localStorage on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    let conversationId = urlParams.get('conversation_id')

    // If no conversation ID in URL, try to get it from lastChatId in localStorage
    if (!conversationId) {
      try {
        conversationId = localStorage.getItem('candidate-lastChatId')
      } catch (error) {
        console.warn('Error reading lastChatId from localStorage:', error)
      }
    }

    if (conversationId) {
      try {
        const storedData = localStorage.getItem(
          `candidate-chat-data-${conversationId}`,
        )
        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData)
            // Validate conversation ID matches
            if (parsedData.conversationId === conversationId) {
              setChatData(parsedData)
            } else {
              console.warn('Conversation ID mismatch in stored data, ignoring')
            }
          } catch (error) {
            console.error('Error parsing stored chat data:', error)
          }
        } else {
          console.warn(
            'No chat data found in localStorage for conversation:',
            conversationId,
          )
        }
      } catch (error) {
        console.error('Error reading chat data from localStorage:', error)
      }
    } else {
      console.warn('No conversation ID available for loading chat data')
    }
  }, [])

  // Scroll to bottom when chat data is loaded
  useEffect(() => {
    if (chatData && textMessagesRef.current) {
      // Use a small delay to ensure the footer is rendered
      setTimeout(() => {
        textMessagesRef.current.scrollToBottom()
      }, 200)
    }
  }, [chatData])

  // Check if at least one survey question has been answered
  const checkSurveyAnswered = () => {
    const rating = ratingRef.current?.value || '0'
    const tags = tagsRef.current?.value || ''
    const feedback = feedbackRef.current?.value || ''

    const answered =
      parseInt(rating) > 0 ||
      tags.trim().length > 0 ||
      feedback.trim().length > 0
    setHasAnsweredSurvey(answered)
  }

  // Determine survey title based on user type
  const getSurveyTitle = () => {
    return deloitteUser
      ? t(
          'chat_ended.title_deloitte',
          "Thank you for using chat! We'd like to know how helpful our advisor was for you.",
        )
      : t(
          'chat_ended.title',
          "Thank you for using chat! We'd like to know how helpful our coach was for you.",
        )
  }

  // Create footer content for the chat ended message
  const getChatEndedMessage = () => {
    if (endedBy === 'coach') {
      return t('chat_ended.closed_by_coach_message', {
        defaultValue: 'Coach has closed this chat.',
      })
    }
    return t('chat_ended.closed_message', {
      defaultValue: 'You have closed this coach chat.',
    })
  }

  const chatEndedFooter = (
    <div className="chat-ended-notice px-6 py-4">
      <Typography
        className={`text-center font-roboto text-base ${getDarkModeClasses('text-cod-gray-700', 'text-gray-300')}`}
      >
        {getChatEndedMessage()}
      </Typography>
    </div>
  )

  // Connection-loss endings: skip survey; show messaging + exit only (CC-UX-026)
  if (isDisconnection) {
    return (
      <div
        className={`chat-ended-container grid h-screen w-full grid-rows-[auto_1fr] border ${getDarkModeClasses('border-cod-gray-200 bg-white', 'border-gray-600 bg-gray-800')}`}
      >
        <div className="chat-ended-header row-start-1">
          <ChatHeader
            selectedCoach={chatData?.selectedCoach}
            chatMode={chatData?.chatMode || 'text'}
            chatStatus="chat_ended"
            showControls={false}
          />
        </div>
        <div className="row-start-2 min-h-0">
          <ConnectionLost
            deloitteUser={deloitteUser}
            disconnectionReason={disconnectionReason}
            exitUrl={effectiveExitUrl}
          />
        </div>
      </div>
    )
  }

  return (
    <form
      method="GET"
      action="/coach-chat-survey"
      target="_top"
      className="flex h-full flex-col"
    >
      <div
        className={`chat-ended-container grid h-screen w-full grid-rows-[auto_200px_1fr_auto] border ${getDarkModeClasses('border-cod-gray-200 bg-white', 'border-gray-600 bg-gray-800')}`}
      >
        {/* Header */}
        <div className="chat-ended-header row-start-1">
          <ChatHeader
            selectedCoach={chatData?.selectedCoach}
            chatMode={chatData?.chatMode || 'text'}
            chatStatus="chat_ended"
            showControls={false}
          />
        </div>

        {/* Chat Messages Container */}
        <div
          className={`chat-ended-messages row-start-2 min-h-0 overflow-y-auto border-b ${getDarkModeClasses('border-cod-gray-300', 'border-gray-600')}`}
        >
          {chatData?.messages && chatData.messages.length > 0 ? (
            <TextMessages
              ref={textMessagesRef}
              messages={chatData.messages}
              footer={chatEndedFooter}
              scrollToBottomOnMount={true}
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Typography
                className={`font-roboto text-base ${getDarkModeClasses('text-cod-gray-500', 'text-gray-400')}`}
              >
                {t('chat_ended.no_messages', {
                  defaultValue: 'No messages to display',
                })}
              </Typography>
            </div>
          )}
        </div>

        {/* Survey Content */}
        <div className="row-start-3 min-h-0 overflow-y-auto">
          <input type="hidden" name="target_url" value={effectiveExitUrl} />
          <div className="min-h-0 flex-1">
            <ChatEndedSurvey
              title={getSurveyTitle()}
              ratingRef={ratingRef}
              tagsRef={tagsRef}
              feedbackRef={feedbackRef}
              onInputChange={checkSurveyAnswered}
            />
          </div>
        </div>

        {/* Action Buttons - Docked to bottom */}
        <div
          className={`chat-ended-actions row-start-4 space-y-3 border-t p-6 ${getDarkModeClasses('border-cod-gray-200', 'border-gray-600')}`}
        >
          <Button
            variant="outline"
            color="secondary"
            size="md"
            fullWidth
            href={`/coach-chat-survey?close=x&target_url=${encodeURIComponent(effectiveExitUrl)}`}
            target="_top"
            className="h-10 rounded-2xl px-6"
          >
            {t('chat_ended.skip_survey', { defaultValue: 'Skip Survey' })}
          </Button>
          <Button
            type="submit"
            variant="solid"
            color="secondary"
            size="md"
            fullWidth
            component="button"
            disabled={!hasAnsweredSurvey}
            className="h-10 rounded-2xl px-6"
          >
            {t('chat_ended.submit', { defaultValue: 'Submit' })}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default ChatEnded
